import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import Layout from '../components/Layout';
import DialogModal from '../components/DialogModal';
import staffAttendanceService from '../services/staffAttendanceService';
import { CheckCircle, FloppyDisk, PencilSimple, Warning, QrCode, ArrowsClockwise, Trash, Star, DownloadSimple, UserMinus } from '@phosphor-icons/react';

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function StaffAttendancePage() {
  const [view, setView] = useState('roster'); // roster or settings
  const [date, setDate] = useState(todayISO());
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({}); // staff_id -> { status, check_in, check_out, remarks }
  const [editingId, setEditingId] = useState(null); // staff_id currently being corrected

  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [ipInput, setIpInput] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [rotating, setRotating] = useState(false);

  const [shifts, setShifts] = useState([]);
  const [shiftsLoading, setShiftsLoading] = useState(true);
  const [shiftForm, setShiftForm] = useState({ name: '', start_time: '08:00:00', end_time: '17:00:00', late_grace_minutes: 15 });
  const [creatingShift, setCreatingShift] = useState(false);
  const [shiftRoster, setShiftRoster] = useState([]);
  const [assigningStaffId, setAssigningStaffId] = useState(null);
  const [biometricPinDrafts, setBiometricPinDrafts] = useState({}); // staff_id -> pin being typed
  const [savingBiometricPinFor, setSavingBiometricPinFor] = useState(null);

  const [autoAbsentDate, setAutoAbsentDate] = useState(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  });
  const [runningAutoAbsent, setRunningAutoAbsent] = useState(false);
  const [exportRange, setExportRange] = useState({ start: date, end: date });
  const [exporting, setExporting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, type, confirmLabel, onConfirm }

  const loadRoster = useCallback(() => {
    setLoading(true);
    staffAttendanceService.getRoster(date)
      .then((data) => {
        setRoster(data.records || []);
        const nextDraft = {};
        (data.records || []).forEach((r) => {
          nextDraft[r.staff_id] = {
            status: r.status || 'present',
            check_in: r.check_in || '',
            check_out: r.check_out || '',
            remarks: r.remarks || '',
          };
        });
        setDraft(nextDraft);
        setEditingId(null);
      })
      .catch((err) => toast.error(err.message || 'Failed to load staff roster'))
      .finally(() => setLoading(false));
  }, [date]);

  const loadSettings = useCallback(() => {
    setSettingsLoading(true);
    staffAttendanceService.getSettings()
      .then((data) => {
        setSettings(data);
        setIpInput(data.allowed_ip_cidr || '');
      })
      .catch((err) => toast.error(err.message || 'Failed to load settings'))
      .finally(() => setSettingsLoading(false));
  }, []);

  const loadShiftsData = useCallback(() => {
    setShiftsLoading(true);
    Promise.all([staffAttendanceService.listShifts(), staffAttendanceService.getRoster(todayISO())])
      .then(([shiftsData, rosterData]) => {
        setShifts(shiftsData.records || []);
        setShiftRoster(rosterData.records || []);
      })
      .catch((err) => toast.error(err.message || 'Failed to load shifts'))
      .finally(() => setShiftsLoading(false));
  }, []);

  useEffect(() => { if (view === 'roster') loadRoster(); }, [view, loadRoster]);
  useEffect(() => { if (view === 'settings') loadSettings(); }, [view, loadSettings]);
  useEffect(() => { if (view === 'shifts') loadShiftsData(); }, [view, loadShiftsData]);

  const updateDraft = (staffId, field, value) => {
    setDraft((prev) => ({ ...prev, [staffId]: { ...prev[staffId], [field]: value } }));
  };

  const handleSaveUnrecorded = async () => {
    const unrecorded = roster.filter((r) => !r.recorded);
    if (unrecorded.length === 0) {
      toast.info('Everyone on this date is already recorded');
      return;
    }
    try {
      setSaving(true);
      const records = unrecorded.map((r) => ({
        staff_id: r.staff_id,
        status: draft[r.staff_id]?.status || 'present',
        check_in: draft[r.staff_id]?.check_in || undefined,
        check_out: draft[r.staff_id]?.check_out || undefined,
        remarks: draft[r.staff_id]?.remarks || undefined,
      }));
      const result = await staffAttendanceService.markBulk(date, records);
      toast.success(result.message || `Recorded ${result.recorded} staff member(s)`);
      loadRoster();
    } catch (error) {
      toast.error(error.message || 'Unable to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const handleCorrect = async (row) => {
    try {
      await staffAttendanceService.correctRecord(row.attendance_id, draft[row.staff_id]?.status || row.status, draft[row.staff_id]?.remarks || '');
      toast.success(`Updated ${row.staff_name}'s attendance`);
      setEditingId(null);
      loadRoster();
    } catch (error) {
      toast.error(error.message || 'Unable to update attendance');
    }
  };

  const handleModeChange = async (mode) => {
    try {
      setSavingSettings(true);
      const data = await staffAttendanceService.updateSettings({ clock_in_mode: mode });
      setSettings(data);
      toast.success(`Clock-in mode set to ${mode === 'qr_required' ? 'QR code required' : 'open (no restriction)'}`);
    } catch (error) {
      toast.error(error.message || 'Unable to update mode');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveIpRange = async () => {
    try {
      setSavingSettings(true);
      const data = await staffAttendanceService.updateSettings({ allowed_ip_cidr: ipInput || null });
      setSettings(data);
      toast.success('Allowed IP range saved');
    } catch (error) {
      toast.error(error.message || 'Unable to save IP range');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRotateQr = async () => {
    try {
      setRotating(true);
      const data = await staffAttendanceService.rotateQrToken();
      setSettings(data);
      toast.success('New QR code generated — print/display the updated one, the old one no longer works');
    } catch (error) {
      toast.error(error.message || 'Unable to generate QR code');
    } finally {
      setRotating(false);
    }
  };

  const handleCreateShift = async (e) => {
    e.preventDefault();
    try {
      setCreatingShift(true);
      await staffAttendanceService.createShift({ ...shiftForm, late_grace_minutes: Number(shiftForm.late_grace_minutes) });
      toast.success('Shift created');
      setShiftForm({ name: '', start_time: '08:00:00', end_time: '17:00:00', late_grace_minutes: 15 });
      loadShiftsData();
    } catch (error) {
      toast.error(error.message || 'Unable to create shift');
    } finally {
      setCreatingShift(false);
    }
  };

  const handleSetDefaultShift = async (shiftId) => {
    try {
      await staffAttendanceService.setDefaultShift(shiftId);
      toast.success('Default shift updated');
      loadShiftsData();
    } catch (error) {
      toast.error(error.message || 'Unable to set default shift');
    }
  };

  const handleDeleteShift = (shiftId) => {
    setConfirmDialog({
      title: 'Deactivate Shift',
      message: 'Deactivate this shift? Staff assigned to it will fall back to the school default (or always-present) until reassigned.',
      type: 'warning',
      confirmLabel: 'Deactivate',
      onConfirm: async () => {
        try {
          await staffAttendanceService.deleteShift(shiftId);
          toast.success('Shift deactivated');
          loadShiftsData();
        } catch (error) {
          toast.error(error.message || 'Unable to deactivate shift');
        }
      },
    });
  };

  const handleAssignShift = async (staffId, shiftId) => {
    try {
      setAssigningStaffId(staffId);
      await staffAttendanceService.assignStaffShift(staffId, shiftId || null);
      toast.success('Shift assignment updated');
      loadShiftsData();
    } catch (error) {
      toast.error(error.message || 'Unable to assign shift');
    } finally {
      setAssigningStaffId(null);
    }
  };

  const handleSaveBiometricPin = async (staffId) => {
    const pin = (biometricPinDrafts[staffId] || '').trim();
    if (!pin) return;
    try {
      setSavingBiometricPinFor(staffId);
      await staffAttendanceService.setBiometricPin(staffId, pin);
      toast.success('Biometric PIN saved');
      setBiometricPinDrafts((prev) => ({ ...prev, [staffId]: '' }));
    } catch (error) {
      toast.error(error.message || 'Unable to save biometric PIN');
    } finally {
      setSavingBiometricPinFor(null);
    }
  };

  const handleRunAutoAbsent = () => {
    setConfirmDialog({
      title: 'Run Auto-Absent',
      message: `Mark every active staff member with no attendance record on ${autoAbsentDate} as absent?`,
      type: 'warning',
      confirmLabel: 'Run',
      onConfirm: async () => {
        try {
          setRunningAutoAbsent(true);
          const result = await staffAttendanceService.triggerAutoAbsent(autoAbsentDate);
          toast.success(result.message || `Marked ${result.marked_absent} staff member(s) absent for ${result.date}`);
          if (view === 'roster') loadRoster();
        } catch (error) {
          toast.error(error.message || 'Unable to run auto-absent');
        } finally {
          setRunningAutoAbsent(false);
        }
      },
    });
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const response = await staffAttendanceService.exportCsv(exportRange.start, exportRange.end);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `staff_attendance_${exportRange.start}_to_${exportRange.end}.csv`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      toast.success('Staff attendance exported');
    } catch (error) {
      toast.error(error.message || 'Unable to export attendance');
    } finally {
      setExporting(false);
    }
  };

  const unrecordedCount = roster.filter((r) => !r.recorded).length;
  const clockInUrl = settings?.current_qr_token ? `${window.location.origin}/staff-clock-in?token=${settings.current_qr_token}` : null;

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Staff Attendance</h1>
            <p>Daily attendance for payroll deduction rules and HR records.</p>
          </div>
          {view === 'roster' && (
            <div className="header-actions">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db' }} />
            </div>
          )}
        </div>

        <div className="view-tabs" style={{ marginBottom: 16 }}>
          <button className={`tab ${view === 'roster' ? 'active' : ''}`} onClick={() => setView('roster')}>Roster</button>
          <button className={`tab ${view === 'shifts' ? 'active' : ''}`} onClick={() => setView('shifts')}>Shifts</button>
          <button className={`tab ${view === 'settings' ? 'active' : ''}`} onClick={() => setView('settings')}>Clock-in Settings</button>
        </div>

        {view === 'roster' && (
          <>
            <div className="table-container">
              {loading ? (
                <div className="loading">Loading roster...</div>
              ) : roster.length === 0 ? (
                <div className="empty-state"><p>No active staff found.</p></div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Staff</th>
                      <th>Status</th>
                      <th>Check In</th>
                      <th>Check Out</th>
                      <th>Remarks</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roster.map((row) => {
                      const isEditing = editingId === row.staff_id;
                      const locked = row.recorded && !isEditing;
                      return (
                        <tr key={row.staff_id}>
                          <td><strong>{row.staff_name}</strong><div style={{ fontSize: 12, color: '#6b7280' }}>{row.position}</div></td>
                          <td>
                            {locked ? (
                              <span className="capitalize">{row.status}</span>
                            ) : (
                              <select
                                value={draft[row.staff_id]?.status || 'present'}
                                onChange={(e) => updateDraft(row.staff_id, 'status', e.target.value)}
                              >
                                <option value="present">Present</option>
                                <option value="absent">Absent</option>
                                <option value="late">Late</option>
                                <option value="excused">Excused</option>
                              </select>
                            )}
                          </td>
                          <td>
                            {locked ? (
                              <>
                                {row.check_in || '—'}
                                {row.via_qr && <span title="Clocked in via gate QR code" style={{ marginLeft: 6 }}><QrCode size={13} color="#16a34a" /></span>}
                                {row.ip_flagged && <span title="Clocked in from an unexpected network — review" style={{ marginLeft: 6 }}><Warning size={13} color="#d97706" /></span>}
                              </>
                            ) : (
                              <input type="time" value={draft[row.staff_id]?.check_in || ''} onChange={(e) => updateDraft(row.staff_id, 'check_in', e.target.value)} disabled={row.recorded} />
                            )}
                          </td>
                          <td>
                            {locked ? (row.check_out || '—') : (
                              <input type="time" value={draft[row.staff_id]?.check_out || ''} onChange={(e) => updateDraft(row.staff_id, 'check_out', e.target.value)} disabled={row.recorded} />
                            )}
                          </td>
                          <td>
                            {locked ? (row.remarks || '—') : (
                              <input type="text" placeholder="Optional" value={draft[row.staff_id]?.remarks || ''} onChange={(e) => updateDraft(row.staff_id, 'remarks', e.target.value)} style={{ width: 120 }} />
                            )}
                          </td>
                          <td>
                            {row.recorded ? (
                              isEditing ? (
                                <button className="btn btn-sm btn-success" onClick={() => handleCorrect(row)}>
                                  <CheckCircle size={14} /> Save
                                </button>
                              ) : (
                                <button className="btn btn-sm btn-ghost" onClick={() => setEditingId(row.staff_id)}>
                                  <PencilSimple size={14} /> Edit
                                </button>
                              )
                            ) : (
                              <span style={{ fontSize: 12, color: '#9ca3af' }}>Unrecorded</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {!loading && roster.length > 0 && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn btn-primary" onClick={handleSaveUnrecorded} disabled={saving || unrecordedCount === 0}>
                  <FloppyDisk size={16} /> {saving ? 'Saving...' : `Save ${unrecordedCount} unrecorded`}
                </button>
              </div>
            )}

            <div className="card" style={{ padding: 16, marginTop: 20, display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <h4 style={{ margin: '0 0 8px' }}>Auto-mark absent</h4>
                <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px', maxWidth: 320 }}>
                  Marks every active staff member with no record on this date as absent. Meant to be run once a day by an external cron caller, or manually here. Weekends are skipped; holidays are not accounted for.
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="date" value={autoAbsentDate} onChange={(e) => setAutoAbsentDate(e.target.value)} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db' }} />
                  <button className="btn btn-secondary" onClick={handleRunAutoAbsent} disabled={runningAutoAbsent}>
                    <UserMinus size={16} /> {runningAutoAbsent ? 'Running...' : 'Run'}
                  </button>
                </div>
              </div>
              <div>
                <h4 style={{ margin: '0 0 8px' }}>Export CSV</h4>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="date" value={exportRange.start} onChange={(e) => setExportRange({ ...exportRange, start: e.target.value })} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db' }} />
                  <span style={{ color: '#6b7280' }}>to</span>
                  <input type="date" value={exportRange.end} onChange={(e) => setExportRange({ ...exportRange, end: e.target.value })} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db' }} />
                  <button className="btn btn-secondary" onClick={handleExportCsv} disabled={exporting}>
                    <DownloadSimple size={16} /> {exporting ? 'Exporting...' : 'Export'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {view === 'shifts' && (
          <div style={{ display: 'grid', gap: 20 }}>
            {shiftsLoading ? (
              <div className="loading">Loading shifts...</div>
            ) : (
              <>
                <div className="card" style={{ padding: 20 }}>
                  <h3 style={{ marginTop: 0 }}>Shifts</h3>
                  <p style={{ fontSize: 13, color: '#6b7280' }}>
                    Staff clocking in after the shift's start time plus its grace period are marked Late instead of Present. One shift per school can be the default, used for any staff member with no shift assigned.
                  </p>
                  {shifts.length === 0 ? (
                    <p style={{ fontSize: 13, color: '#9ca3af' }}>No shifts yet — staff without a shift always clock in as Present.</p>
                  ) : (
                    <table className="data-table">
                      <thead><tr><th>Name</th><th>Start</th><th>End</th><th>Grace (min)</th><th>Default</th><th>Actions</th></tr></thead>
                      <tbody>
                        {shifts.map((s) => (
                          <tr key={s.id}>
                            <td>{s.name}</td>
                            <td>{s.start_time}</td>
                            <td>{s.end_time}</td>
                            <td>{s.late_grace_minutes}</td>
                            <td>{s.is_default ? <Star size={16} color="#d97706" weight="fill" /> : (
                              <button className="btn btn-sm btn-ghost" onClick={() => handleSetDefaultShift(s.id)}>Set default</button>
                            )}</td>
                            <td>
                              <button className="btn btn-sm btn-ghost" onClick={() => handleDeleteShift(s.id)}><Trash size={14} /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  <form onSubmit={handleCreateShift} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 16 }}>
                    <label style={{ fontSize: 12 }}>Name<br />
                      <input type="text" required value={shiftForm.name} onChange={(e) => setShiftForm({ ...shiftForm, name: e.target.value })} placeholder="e.g. Teaching Staff" style={{ padding: 8, borderRadius: 6, border: '1px solid #d1d5db' }} />
                    </label>
                    <label style={{ fontSize: 12 }}>Start time<br />
                      <input type="time" step="1" required value={shiftForm.start_time} onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })} style={{ padding: 8, borderRadius: 6, border: '1px solid #d1d5db' }} />
                    </label>
                    <label style={{ fontSize: 12 }}>End time<br />
                      <input type="time" step="1" required value={shiftForm.end_time} onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })} style={{ padding: 8, borderRadius: 6, border: '1px solid #d1d5db' }} />
                    </label>
                    <label style={{ fontSize: 12 }}>Grace (min)<br />
                      <input type="number" min="0" value={shiftForm.late_grace_minutes} onChange={(e) => setShiftForm({ ...shiftForm, late_grace_minutes: e.target.value })} style={{ padding: 8, borderRadius: 6, border: '1px solid #d1d5db', width: 80 }} />
                    </label>
                    <button className="btn btn-primary" type="submit" disabled={creatingShift}>{creatingShift ? 'Creating...' : 'Add shift'}</button>
                  </form>
                </div>

                <div className="card" style={{ padding: 20 }}>
                  <h3 style={{ marginTop: 0 }}>Assign staff to shifts</h3>
                  <table className="data-table">
                    <thead><tr><th>Staff</th><th>Shift</th><th>Biometric PIN</th></tr></thead>
                    <tbody>
                      {shiftRoster.map((row) => (
                        <tr key={row.staff_id}>
                          <td><strong>{row.staff_name}</strong><div style={{ fontSize: 12, color: '#6b7280' }}>{row.position}</div></td>
                          <td>
                            <select
                              value={row.shift_id || ''}
                              onChange={(e) => handleAssignShift(row.staff_id, e.target.value)}
                              disabled={assigningStaffId === row.staff_id}
                            >
                              <option value="">Unassigned (school default)</option>
                              {shifts.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <input
                                type="text"
                                placeholder="PIN from device enrollment"
                                value={biometricPinDrafts[row.staff_id] || ''}
                                onChange={(e) => setBiometricPinDrafts((prev) => ({ ...prev, [row.staff_id]: e.target.value }))}
                                style={{ padding: 6, borderRadius: 6, border: '1px solid #d1d5db', width: 160 }}
                              />
                              <button
                                className="btn btn-secondary"
                                type="button"
                                disabled={savingBiometricPinFor === row.staff_id || !(biometricPinDrafts[row.staff_id] || '').trim()}
                                onClick={() => handleSaveBiometricPin(row.staff_id)}
                              >
                                {savingBiometricPinFor === row.staff_id ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {view === 'settings' && (
          <div style={{ display: 'grid', gap: 20, maxWidth: 640 }}>
            {settingsLoading ? (
              <div className="loading">Loading settings...</div>
            ) : (
              <>
                <div className="card" style={{ padding: 20 }}>
                  <h3 style={{ marginTop: 0 }}>Self clock-in mode</h3>
                  <p style={{ fontSize: 13, color: '#6b7280' }}>
                    How strict should self-service clock-in/out be? This applies to every staff member at this school.
                  </p>
                  <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                    <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
                      <input type="radio" checked={settings?.clock_in_mode === 'open'} onChange={() => handleModeChange('open')} disabled={savingSettings} style={{ marginTop: 3 }} />
                      <span>
                        <strong>Open</strong>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>Staff can clock in/out from anywhere with one tap. The network address is still logged for HR to review, but nothing is blocked.</div>
                      </span>
                    </label>
                    <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
                      <input type="radio" checked={settings?.clock_in_mode === 'qr_required'} onChange={() => handleModeChange('qr_required')} disabled={savingSettings} style={{ marginTop: 3 }} />
                      <span>
                        <strong>QR code required</strong>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>Staff must scan a QR code posted at the school entrance to clock in/out — proves they were physically on-site.</div>
                      </span>
                    </label>
                  </div>
                </div>

                <div className="card" style={{ padding: 20 }}>
                  <h3 style={{ marginTop: 0 }}>Allowed network range (optional, soft signal)</h3>
                  <p style={{ fontSize: 13, color: '#6b7280' }}>
                    If set, clock-ins from outside this range are flagged (⚠) in the roster for HR to review — never blocked. Leave blank to disable. Comma-separate multiple ranges.
                  </p>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <input
                      type="text"
                      value={ipInput}
                      onChange={(e) => setIpInput(e.target.value)}
                      placeholder="e.g. 41.203.64.0/24"
                      style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
                    />
                    <button className="btn btn-secondary" onClick={handleSaveIpRange} disabled={savingSettings}>Save</button>
                  </div>
                </div>

                {settings?.clock_in_mode === 'qr_required' && (
                  <div className="card" style={{ padding: 20 }}>
                    <h3 style={{ marginTop: 0 }}>Gate QR code</h3>
                    <p style={{ fontSize: 13, color: '#6b7280' }}>
                      Print this and display it at the school entrance. Staff scan it with their phone camera to clock in/out.
                      Rotating it invalidates the old poster immediately.
                    </p>
                    {clockInUrl ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12, marginTop: 12 }}>
                        <div style={{ background: '#fff', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' }}>
                          <QRCode value={clockInUrl} size={180} />
                        </div>
                        <div style={{ fontSize: 12, color: '#9ca3af' }}>
                          Valid for {settings.qr_token_ttl_minutes} minutes from generation
                          {settings.qr_token_generated_at && ` (generated ${new Date(settings.qr_token_generated_at).toLocaleString()})`}
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: '#dc2626', margin: '12px 0' }}>No valid QR code yet — generate one below.</div>
                    )}
                    <button className="btn btn-primary" onClick={handleRotateQr} disabled={rotating} style={{ marginTop: 12 }}>
                      <ArrowsClockwise size={16} /> {rotating ? 'Generating...' : clockInUrl ? 'Rotate QR code' : 'Generate QR code'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <DialogModal
        isOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        type={confirmDialog?.type || 'warning'}
        primaryAction={{ label: confirmDialog?.confirmLabel || 'Confirm', onClick: () => confirmDialog?.onConfirm() }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default StaffAttendancePage;
